import { createSelector } from '@reduxjs/toolkit';
import { SafetyEventStatuses, type IMinimalSafetyEventForClient } from '@d-orca/shared/trusstor';
import type { IRootState } from '@store/slices';
import { professionColor } from '@utils/professionColor';
import { selectSafetyEvents, selectProfessions } from './monitor.base.selectors';

/** A safety row's floor label when the alert has no floor. */
export const NO_FLOOR_LABEL = '—';

/**
 * Per-floor safety summary used by the side-rail alert badge. `count` is the
 * total active events on the floor; `hasCritical` flips to true if any event
 * has `isCritical`. Mirrors trusstor's `SafetyIcon` severity rule:
 *   any critical → red glyph, otherwise orange.
 */
export type FloorAlertsSummary = {
  count: number;
  hasCritical: boolean;
};

/**
 * Active safety events grouped per floor. Single source of truth for the
 * side-rail alert badge: the badge variant pulls `count + hasCritical`
 * via `selectAlertsByFloorId`, the hover tooltip pulls the underlying
 * event list. Both selectors below derive from this map so we walk the
 * `safetyEvents` array once per SSE update.
 *
 * Mirrors the data trusstor's `SafetyIcon` tooltip reads
 * (`SafetyIcon.tsx:71-83`): each line shows the translated
 * `safetyViolationType` of an active event.
 */
export const selectSafetyEventsByFloorId = createSelector(
  [selectSafetyEvents],
  (events) => {
    const out = new Map<string, IMinimalSafetyEventForClient[]>();
    for (const e of events) {
      const fid = e.floorId;
      if (!fid) continue;
      const list = out.get(fid) ?? [];
      list.push(e);
      out.set(fid, list);
    }
    return out;
  },
);

export const selectAlertsByFloorId = createSelector(
  [selectSafetyEventsByFloorId],
  (eventsByFloor): Map<string, FloorAlertsSummary> => {
    const out = new Map<string, FloorAlertsSummary>();
    eventsByFloor.forEach((events, fid) => {
      out.set(fid, {
        count: events.length,
        hasCritical: events.some((e) => e.isCritical === true),
      });
    });
    return out;
  },
);

/**
 * Per-row shape consumed by `SafetyAlertRow`. The selector exposes raw
 * fields and lets the component handle i18n / Intl formatting (mirrors
 * the workforce/activity selectors above) — keeps Redux state pure of
 * locale-bound strings.
 *
 * Chip resolution priority mirrors trusstor's `Alerts.tsx:130–139`:
 * profession → trade → manager. Profession's chip colour comes from the
 * full profession record (`selectProfessions`) since `IMinimizedProfession`
 * itself doesn't carry `backgroundColor`.
 */
export type SafetyRow = {
  id: string;
  isCritical: boolean;
  /** Raw enum key — render via `t('monitor.safetyViolationType.' + type)`. */
  type: IMinimalSafetyEventForClient['safetyViolationType'];
  /** Epoch ms — format JST in the component. */
  timestamp: number;
  floorLabel: string;
  /** `null` when none of profession/trade/manager are present (chip hidden). */
  chip:
    | { kind: 'trade'; tradeId: string; tradeGroup?: string; color: string }
    | { kind: 'manager'; name: string; color: string }
    | null;
};

/** A trade's chip on a safety row (live and replay rows alike). */
export const tradeChip = (trade: {
  tradeId: string;
  tradeGroup?: string;
  backgroundColor?: string | null;
}): NonNullable<SafetyRow['chip']> => ({
  kind: 'trade',
  tradeId: trade.tradeId,
  tradeGroup: trade.tradeGroup,
  color: professionColor(trade.backgroundColor),
});

function rowFromEvent(
  e: IMinimalSafetyEventForClient,
  profById: Map<string, { backgroundColor?: string; tradeId?: string; tradeGroup?: string }>,
): SafetyRow {
  let chip: SafetyRow['chip'] = null;
  if (e.profession) {
    const full = profById.get(e.profession._id);
    chip = {
      kind: 'trade',
      tradeId: full?.tradeId ?? e.profession.tradeId,
      tradeGroup: full?.tradeGroup ?? e.profession.tradeGroup,
      color: professionColor(full?.backgroundColor),
    };
  } else if (e.trade) {
    chip = tradeChip(e.trade);
  } else if (e.manager) {
    chip = {
      kind: 'manager',
      name: e.manager.name,
      color: professionColor(e.manager.backgroundColor),
    };
  }
  return {
    id: e._id,
    isCritical: e.isCritical,
    type: e.safetyViolationType,
    timestamp: e.timestampCreated,
    floorLabel: e.floorNick ?? NO_FLOOR_LABEL,
    chip,
  };
}

function professionLookup(
  professions: ReadonlyArray<{ _id: string; backgroundColor?: string; tradeId?: string; tradeGroup?: string }>,
): Map<string, { backgroundColor?: string; tradeId?: string; tradeGroup?: string }> {
  return new Map(professions.map((p) => [p._id, p]));
}

/**
 * Active events for the LIVE section. Critical first, then newest-created.
 * Mirrors trusstor's `Alerts.tsx:107–111`.
 */
export const selectActiveSafetyRows = createSelector(
  [selectSafetyEvents, selectProfessions],
  (events, professions): SafetyRow[] => {
    const profById = professionLookup(
      professions as ReadonlyArray<{ _id: string; backgroundColor?: string; tradeId?: string; tradeGroup?: string }>,
    );
    return events
      .filter((e) => e.status === 'active')
      .slice()
      .sort((a, b) => {
        if (a.isCritical !== b.isCritical) return a.isCritical ? -1 : 1;
        return b.timestampCreated - a.timestampCreated;
      })
      .map((e) => rowFromEvent(e, profById));
  },
);

/**
 * Closed events for the EVENTS LOG section. Newest-closed first.
 * Mirrors trusstor's `Alerts.tsx:112–116`.
 */
export const selectClosedSafetyRows = createSelector(
  [selectSafetyEvents, selectProfessions],
  (events, professions): SafetyRow[] => {
    const profById = professionLookup(
      professions as ReadonlyArray<{ _id: string; backgroundColor?: string; tradeId?: string; tradeGroup?: string }>,
    );
    return events
      .filter((e) => e.status === 'close')
      .slice()
      .sort(
        // `timestampClosed` is null for events that snuck through with the
        // wrong status — fall back to created so they still sort sensibly.
        (a, b) =>
          (b.timestampClosed ?? b.timestampCreated) -
          (a.timestampClosed ?? a.timestampCreated),
      )
      .map((e) => rowFromEvent(e, profById));
  },
);

/**
 * Whether the live feed still has this event open — the alert dialog's End
 * Event gate. Fresher than the dialog's fetched detail, which a close from
 * anywhere (another manager, an auto-close) does not update.
 */
export const selectIsSafetyEventActive = (state: IRootState, eventId: string): boolean =>
  selectSafetyEvents(state).some(
    (event) => event._id === eventId && event.status === SafetyEventStatuses.active,
  );
